import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users, Trash2, ArrowLeft, Search, Upload, X, FileUp } from "lucide-react";
import Navbar from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  free: "bg-muted text-muted-foreground",
  monthly: "bg-accent/10 text-brand-accent",
  yearly: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function parseSubscriberLines(text) {
  const entries = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const tokens = line.split(/[,;\t]/).map((t) => t.trim().replace(/^["']|["']$/g, "")).filter(Boolean);
    const email = tokens.find((t) => EMAIL_RE.test(t));
    if (!email) continue;
    const name = tokens.filter((t) => t !== email && !EMAIL_RE.test(t)).join(" ");
    entries.push({ email, name });
  }
  return entries;
}

export default function AdminSubscribers() {
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importSource, setImportSource] = useState("Purchased Leads");
  const [importing, setImporting] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchSubscribers();
  }, [tier]);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const params = tier !== "all" ? `?tier=${tier}` : "";
      const res = await axios.get(`${API}/admin/subscribers${params}`, { withCredentials: true, headers });
      setSubscribers(res.data || []);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { withCredentials: true, headers });
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      setDeleteId(null);
      toast.success("Subscriber removed");
    } catch {
      toast.error("Failed to remove subscriber");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText((prev) => (prev ? prev + "\n" : "") + ev.target.result);
    reader.readAsText(file);
    e.target.value = "";
  };

  const parsedEntries = parseSubscriberLines(importText);

  const handleImport = async () => {
    if (parsedEntries.length === 0) {
      toast.error("No valid email addresses found. Paste one email per line, or Name, Email.");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(
        `${API}/admin/subscribers/import`,
        { subscribers: parsedEntries, source: importSource || "imported" },
        { withCredentials: true, headers }
      );
      const { imported, skipped_existing, invalid } = res.data;
      toast.success(
        `Imported ${imported} new subscriber${imported === 1 ? "" : "s"}` +
        (skipped_existing ? ` · ${skipped_existing} already existed` : "") +
        (invalid ? ` · ${invalid} invalid` : "")
      );
      setImportText("");
      setShowImport(false);
      fetchSubscribers();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const filtered = subscribers.filter((s) =>
    !search || s.email.includes(search.toLowerCase()) || (s.name && s.name.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <p className="overline mb-1">Admin</p>
              <h1 className="font-cormorant text-3xl font-semibold text-foreground">Subscribers</h1>
            </div>
          </div>
          <button
            onClick={() => setShowImport((v) => !v)}
            data-testid="import-leads-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors duration-200"
          >
            {showImport ? <X size={15} /> : <Upload size={15} />}
            {showImport ? "Close" : "Import Leads"}
          </button>
        </div>

        {/* Import Panel */}
        {showImport && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-5 mb-8"
            data-testid="import-panel"
          >
            <h2 className="font-cormorant text-2xl font-semibold text-foreground mb-1">Import purchased leads</h2>
            <p className="font-manrope text-xs text-muted-foreground mb-4">
              Paste emails below (one per line, or <span className="font-medium">Name, Email</span>) or upload a CSV file.
              Duplicates are skipped automatically.
            </p>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={6}
              placeholder={"jane@example.com\nJohn Smith, john@example.com\n..."}
              data-testid="import-textarea"
              className="w-full px-4 py-3 rounded-lg border border-border bg-background font-manrope text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-accent/40 mb-3"
            />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-border font-manrope text-sm font-medium text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                <FileUp size={15} />
                Upload CSV
                <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" data-testid="import-file-input" />
              </label>
              <input
                type="text"
                value={importSource}
                onChange={(e) => setImportSource(e.target.value)}
                placeholder="Source label (e.g. LinkedIn, Purchased Leads)"
                data-testid="import-source-input"
                className="flex-1 px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
              <button
                onClick={handleImport}
                disabled={importing || parsedEntries.length === 0}
                data-testid="import-submit-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-50 transition-colors"
              >
                <Upload size={14} />
                {importing ? "Importing..." : `Import ${parsedEntries.length || ""}`.trim()}
              </button>
            </div>
            {importText && (
              <p className="font-manrope text-xs text-muted-foreground mt-2" data-testid="import-preview-count">
                {parsedEntries.length} valid email{parsedEntries.length === 1 ? "" : "s"} detected
              </p>
            )}
          </motion.div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total", value: subscribers.length, color: "" },
            { label: "Free", value: subscribers.filter((s) => s.tier === "free").length, color: "text-muted-foreground" },
            { label: "Paid", value: subscribers.filter((s) => s.tier !== "free").length, color: "text-brand-accent" },
          ].map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-xl p-4 text-center">
              <p className={`font-cormorant text-3xl font-semibold text-foreground ${stat.color}`}>{stat.value}</p>
              <p className="font-manrope text-xs text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="subscriber-search"
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
            />
          </div>
          <div className="flex items-center gap-2">
            {["all", "free", "monthly", "yearly"].map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                data-testid={`tier-filter-${t}`}
                className={`px-3 py-2 rounded-full font-manrope text-sm font-medium transition-colors ${
                  tier === t ? "bg-brand-accent text-white" : "border border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Subscribers List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-xl">
            <Users size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="font-cormorant text-2xl text-foreground mb-2">No subscribers found</p>
            <p className="font-manrope text-sm text-muted-foreground">Share your newsletter to start growing your audience.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full" data-testid="subscribers-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground">Subscriber</th>
                  <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground hidden sm:table-cell">Joined</th>
                  <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground hidden md:table-cell">Source</th>
                  <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground">Tier</th>
                  <th className="py-3 px-5 w-16" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub, i) => (
                  <motion.tr
                    key={sub.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    data-testid={`subscriber-row-${sub.id}`}
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-cormorant text-sm font-semibold text-foreground shrink-0">
                          {(sub.name || sub.email)[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          {sub.name && <p className="font-manrope text-sm font-medium text-foreground">{sub.name}</p>}
                          <p className="font-manrope text-xs text-muted-foreground truncate">{sub.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-5 hidden sm:table-cell">
                      <span className="font-manrope text-xs text-muted-foreground">{formatDate(sub.subscribed_at)}</span>
                    </td>
                    <td className="py-3.5 px-5 hidden md:table-cell">
                      <span className="font-manrope text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground" data-testid={`subscriber-source-${sub.id}`}>
                        {sub.source || "website"}
                      </span>
                    </td>
                    <td className="py-3.5 px-5">
                      <span className={`font-manrope text-xs px-2 py-1 rounded-full font-medium ${TIER_STYLES[sub.tier] || TIER_STYLES.free}`}>
                        {sub.tier}
                      </span>
                    </td>
                    <td className="py-3.5 px-5">
                      {deleteId === sub.id ? (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => handleDelete(sub.id)} className="px-2 py-1 rounded bg-destructive text-destructive-foreground font-manrope text-xs">
                            Confirm
                          </button>
                          <button onClick={() => setDeleteId(null)} className="px-2 py-1 rounded border border-border font-manrope text-xs text-muted-foreground">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteId(sub.id)}
                          data-testid={`delete-subscriber-${sub.id}`}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
